import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { setTypeText } from '@platform/features/workspaces/builder';
import { globalOverrideApi, useResetGlobalModuleMutation, useUpdateGlobalModuleMutation } from '@platform/state/api';
import { useAppDispatch, useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { useFailToast } from '../../system/useFailToast';
import type { IGlobalModuleActions } from './IGlobalModuleActions';
import type { IGlobalTypeRow } from './IGlobalTypeRow';

const isMain = (s: IGlobalTypeRow) => !s.hostName;

/**
 * Дії «Каталогу модулів» рівня «Глобально» (прототип v4 cmRows при cscope==='global'; ADR-027): головний тип — PATCH /platform/modules/{key}
 * (назва, збіг з опублікованою — "" — повертає опубліковану; увімкнення), дочірній — глобальна назва типу (перевизначення з If-Match
 * його версії); «Скинути» після підтвердження — POST /platform/modules/{key}/reset?recordType= (рядок шаблону, як у прототипі: перевизначення
 * типу; головного — ще й глобальні назва й доступність модуля).
 */
export function useGlobalModuleActions(): IGlobalModuleActions {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const lang = useAppSelector(selectLang);
  const fail = useFailToast();
  const [update] = useUpdateGlobalModuleMutation();
  const [resetModule] = useResetGlobalModuleMutation();
  const renameType = useCallback(async (s: IGlobalTypeRow, value: string) => {
    const rt = s.row.rt.key;
    const doc = await dispatch(globalOverrideApi.endpoints.globalRecordTypeOverride.initiate({ rt })).unwrap();
    const patch = setTypeText(doc.patch, 'name', lang, value, s.published);
    await dispatch(globalOverrideApi.endpoints.saveGlobalRecordTypeOverride.initiate({ rt, module: s.row.def.module.key, patch, version: doc.version })).unwrap();
  }, [dispatch, lang]);
  const rename = useCallback((s: IGlobalTypeRow, value: string) => {
    const text = value.trim();
    if (text === s.name) return;
    if (!isMain(s)) { renameType(s, text).catch(fail); return; }
    update({ key: s.row.def.module.key, body: { name: !text || text === s.published ? '' : text } }).unwrap().catch(fail);
  }, [renameType, update, fail]);
  const toggle = useCallback((s: IGlobalTypeRow, on: boolean) => {
    update({ key: s.row.def.module.key, body: { enabled: on } }).unwrap().catch(fail);
  }, [update, fail]);
  const reset = useCallback((s: IGlobalTypeRow) => {
    if (!window.confirm(t('sysCust.catalog.resetConfirm', { name: s.name }))) return;
    resetModule({ key: s.row.def.module.key, recordType: s.row.rt.key }).unwrap().catch(fail);
  }, [t, resetModule, fail]);
  return useMemo(() => ({ rename, toggle, reset }), [rename, toggle, reset]);
}
